import type { ServiceAccountHeaders } from "@/api";
import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { defaultRetry, defaultThrowOnError } from "@/queries/errors";
import { autoPaginate } from "@/queries/pagination";
import { keyWithPrincipal, serviceAccountPrincipalKey } from "@/queries/principal-key";
import { toPlainSecret, type SecretMountType } from "@/queries/secret-queries";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ListSecretsRequest, SecretScope } from "gitpod-next-api/gitpod/v1/secret_pb";

export const serviceAccountSecretKeys = {
    list: (serviceAccountId: string) =>
        keyWithPrincipal(
            ["secrets", "list", "serviceAccount", serviceAccountId],
            serviceAccountPrincipalKey(serviceAccountId),
        ),
};

export type UseListServiceAccountSecretsParams = {
    serviceAccountId: string;
    headers?: ServiceAccountHeaders;
    enabled?: boolean;
};

export const useListServiceAccountSecrets = ({
    serviceAccountId,
    headers,
    enabled = true,
}: UseListServiceAccountSecretsParams) => {
    const api = useGitpodAPI();

    return useQuery({
        queryKey: serviceAccountSecretKeys.list(serviceAccountId),
        queryFn: async () => {
            const secrets = await autoPaginate(
                (params) => api.secretService.listSecrets(params, headers ? { headers: headers.headers } : undefined),
                new ListSecretsRequest({
                    filter: {
                        scope: new SecretScope({
                            scope: {
                                value: serviceAccountId,
                                case: "serviceAccountId",
                            },
                        }),
                    },
                }),
                (response) => response.secrets,
            );

            return {
                secrets: secrets.map(toPlainSecret),
            };
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        enabled: enabled && !!serviceAccountId && !!headers,
        staleTime: 2000,
    });
};

export type UseCreateServiceAccountSecretParams = {
    headers?: ServiceAccountHeaders;
};

export const useCreateServiceAccountSecret = ({ headers }: UseCreateServiceAccountSecretParams) => {
    const api = useGitpodAPI();
    const client = useQueryClient();

    return useMutation({
        mutationFn: async ({
            serviceAccountId,
            secretName,
            plaintextValue,
            mount,
        }: {
            serviceAccountId: string;
            secretName: string;
            plaintextValue: string;
            mount: SecretMountType;
        }) => {
            if (!headers) {
                throw new Error("Service account headers required");
            }

            const { secret } = await api.secretService.createSecret(
                {
                    name: secretName,
                    scope: new SecretScope({
                        scope: {
                            value: serviceAccountId,
                            case: "serviceAccountId",
                        },
                    }),
                    value: plaintextValue,
                    mount: mount,
                },
                { headers: headers.headers },
            );

            if (secret) {
                // Invalidate the list query to refetch
                await client.invalidateQueries({ queryKey: serviceAccountSecretKeys.list(serviceAccountId) });
                return toPlainSecret(secret);
            }

            throw new Error("Failed to create secret");
        },
        throwOnError: defaultThrowOnError,
    });
};

export type UseUpdateServiceAccountSecretValueParams = {
    headers?: ServiceAccountHeaders;
};

export const useUpdateServiceAccountSecretValue = ({ headers }: UseUpdateServiceAccountSecretValueParams) => {
    const api = useGitpodAPI();
    const client = useQueryClient();

    return useMutation({
        mutationFn: async ({
            serviceAccountId,
            secretId,
            plaintextValue,
        }: {
            serviceAccountId: string;
            secretId: string;
            plaintextValue: string;
        }) => {
            if (!headers) {
                throw new Error("Service account headers required");
            }

            await api.secretService.updateSecretValue(
                {
                    secretId: secretId,
                    value: plaintextValue,
                },
                { headers: headers.headers },
            );

            // Invalidate the list query to refetch
            await client.invalidateQueries({ queryKey: serviceAccountSecretKeys.list(serviceAccountId) });
        },
        throwOnError: defaultThrowOnError,
    });
};

export type UseDeleteServiceAccountSecretParams = {
    headers?: ServiceAccountHeaders;
};

export const useDeleteServiceAccountSecret = ({ headers }: UseDeleteServiceAccountSecretParams) => {
    const api = useGitpodAPI();
    const client = useQueryClient();

    return useMutation({
        mutationFn: async ({ serviceAccountId, secretId }: { serviceAccountId: string; secretId: string }) => {
            if (!headers) {
                throw new Error("Service account headers required");
            }

            await api.secretService.deleteSecret(
                {
                    secretId: secretId,
                },
                { headers: headers.headers },
            );

            // Invalidate the list query to refetch
            await client.invalidateQueries({ queryKey: serviceAccountSecretKeys.list(serviceAccountId) });
        },
        throwOnError: defaultThrowOnError,
    });
};
